import { useMemo, useState, useCallback, useEffect } from "react";
import { AlertCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn, formatDate } from "@/lib/utils";
import { calculateChargeableDays } from "@/lib/days";
import type { AvailabilityData } from "@/lib/api";

interface AvailabilityCalendarProps {
  monthHeadingLevel?: 3 | 4;
  availability: AvailabilityData;
  onDateSelect?: (from: string, to: string) => void;
  selectedFrom?: string | null;
  selectedTo?: string | null;
  rentalType?: "driving" | "chauffeured" | "event";
  // Task #2940 follow-up — when true, the start date pill is fixed
  // (used by RequestChangeModal for in-progress trips). Any day click
  // updates `to` only; clicks before `selectedFrom` are ignored
  // instead of replacing the start. The pill still renders so the
  // user can see the locked anchor.
  lockFrom?: boolean;
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

/**
 * Format a Date as a local-calendar `YYYY-MM-DD` string.
 *
 * Task #2481 — the previous implementation used `date.toISOString().split("T")[0]`,
 * which converts the local-midnight Date built by `new Date(year, month, d)` into
 * UTC and chops to 10 chars. For any viewer west or east of UTC this shifts the
 * day component by ±1 (e.g. local Aug 4 in BST becomes "2026-08-03"), which then
 * mis-compares against the YYYY-MM-DD strings the API returns and shades the
 * wrong cells as blocked. We render the local Y/M/D directly to keep the
 * comparison shape-stable across timezones.
 */
function toLocalDateStr(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Defensive normaliser — the public /availability endpoint already returns
 * `YYYY-MM-DD` (Task #2481, see api-server src/routes/cars.ts), but if a stray
 * ISO timestamp ever sneaks back in (legacy cache, future endpoint variant),
 * we still want a same-shape comparison rather than a string-prefix bug.
 */
function toDayStr(s: string): string {
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function isDateBlocked(date: Date, blockedDates: AvailabilityData["blockedDates"]) {
  const dateStr = toLocalDateStr(date);
  return blockedDates.some(bd => dateStr >= toDayStr(bd.start_date) && dateStr <= toDayStr(bd.end_date));
}

function formatMonth(year: number, month: number) {
  return formatDate(`${year}-${String(month + 1).padStart(2, "0")}-01`, { month: "long", year: "numeric" });
}

function daysBetween(from: string, to: string): number {
  return calculateChargeableDays(from, to);
}

function hasBlockedDateInRange(from: string, to: string, blockedDates: AvailabilityData["blockedDates"]): boolean {
  // Task #2481 — `from` and `to` are always `YYYY-MM-DD` (built from the
  // calendar grid), but `bd.start_date` / `bd.end_date` came from the API
  // and historically included an ISO time suffix. Slice both sides to the
  // YYYY-MM-DD prefix so the comparison is shape-stable even if a stray
  // timestamp ever sneaks through.
  return blockedDates.some(bd => toDayStr(bd.start_date) <= to && toDayStr(bd.end_date) >= from);
}

// Test-only re-exports (Task #2481). The helpers above are module-private
// for the React component, but the off-by-one regression actually lived in
// these two functions, so we surface them under explicit `*ForTest` names
// for the unit suite. Suffix avoids accidental product use.
export const isDateBlockedForTest = isDateBlocked;
export const hasBlockedDateInRangeForTest = hasBlockedDateInRange;

/** C3 test helper — returns the earliest selectable date for a given `today`
 *  value and `advanceNoticeDays`. Mirrors the component's earliestDate
 *  calculation so tests can assert the start-of-day normalisation without
 *  rendering the full calendar. */
export function computeEarliestDateForTest(today: Date, advanceNoticeDays: number): Date {
  const d = new Date(today);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + advanceNoticeDays);
  return d;
}

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export function AvailabilityCalendar({ availability, onDateSelect, selectedFrom: externalFrom, selectedTo: externalTo, rentalType, lockFrom = false, monthHeadingLevel = 4 }: AvailabilityCalendarProps) {
  const MonthHeading = monthHeadingLevel === 3 ? "h3" : "h4";
  const { t } = useTranslation();
  const today = new Date();
  const minBookingError = (selectedDays: number) =>
    `${t("dateRange.min_booking_is", { count: minDays })} ${t("dateRange.you_selected", { count: selectedDays })}`;
  // Seed the visible month from the currently-selected `from` date so the
  // calendar opens on the month the renter has already picked, not on
  // today's month. Only used at mount — once open, the user can navigate
  // freely without us snapping back. Falls back to today when no valid
  // selection exists yet. (#824 follow-up)
  const [baseMonth, setBaseMonth] = useState(() => {
    if (externalFrom) {
      const seed = new Date(`${externalFrom}T00:00:00`);
      if (!Number.isNaN(seed.getTime())) {
        return { year: seed.getFullYear(), month: seed.getMonth() };
      }
    }
    return { year: today.getFullYear(), month: today.getMonth() };
  });
  const [internalFrom, setInternalFrom] = useState<string | null>(null);
  const [internalTo, setInternalTo] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const isControlled = externalFrom !== undefined;
  const selectedFrom = isControlled ? (externalFrom || null) : internalFrom;
  const selectedTo = isControlled ? (externalTo || null) : internalTo;

  useEffect(() => {
    if (isControlled) {
      setInternalFrom(externalFrom || null);
      setInternalTo(externalTo || null);
    }
  }, [isControlled, externalFrom, externalTo]);

  const advanceNotice = availability.window?.advance_notice_days || 0;
  const minDays = (rentalType === "chauffeured" || rentalType === "event") ? 1 : (availability.window?.min_booking_days || 1);
  // C3 — normalise to start-of-day BEFORE adding advance_notice_days so the
  // current calendar day is selectable when advance notice allows it. Without
  // this, a viewer at e.g. 11 PM whose `today` carries time-of-day info would
  // have their "today" compared against a grid cell built from local midnight,
  // making today appear in the past and pushing the earliest selectable date
  // to tomorrow even when advance_notice_days === 0.
  const earliestDate = new Date(today);
  earliestDate.setHours(0, 0, 0, 0);
  earliestDate.setDate(earliestDate.getDate() + advanceNotice);

  const months = useMemo(() => {
    const m1 = { year: baseMonth.year, month: baseMonth.month };
    let m2month = baseMonth.month + 1;
    let m2year = baseMonth.year;
    if (m2month > 11) { m2month = 0; m2year++; }
    return [m1, { year: m2year, month: m2month }];
  }, [baseMonth]);

  const handlePrev = () => {
    let m = baseMonth.month - 1;
    let y = baseMonth.year;
    if (m < 0) { m = 11; y--; }
    if (y > today.getFullYear() - 1) setBaseMonth({ year: y, month: m });
  };

  const handleNext = () => {
    let m = baseMonth.month + 1;
    let y = baseMonth.year;
    if (m > 11) { m = 0; y++; }
    setBaseMonth({ year: y, month: m });
  };

  const handleDayClick = useCallback((dateStr: string) => {
    setValidationError(null);

    // Chauffeured is a single-day, multi-hour service: lock the drop-off
    // date to the pick-up date. Any day click selects that single day (in
    // lockFrom mode the anchor stays put). A cross-midnight chauffeured
    // booking can't be priced by the per-day-hours model, so a range is
    // never allowed here.
    if (rentalType === "chauffeured") {
      const day = lockFrom && selectedFrom ? selectedFrom : dateStr;
      if (!isControlled) {
        setInternalFrom(day);
        setInternalTo(day);
      }
      onDateSelect?.(day, day);
      return;
    }

    // Task #2940 follow-up — in lockFrom mode the start pill is fixed
    // and every click only moves the `to` date. Ignore clicks before
    // (or equal to, when that'd shrink to 0 days) the locked anchor.
    if (lockFrom && selectedFrom) {
      if (dateStr < selectedFrom) return;
      const duration = daysBetween(selectedFrom, dateStr);
      if (duration < minDays) {
        setValidationError(minBookingError(duration));
        return;
      }
      if (hasBlockedDateInRange(selectedFrom, dateStr, availability.blockedDates)) {
        setValidationError(t("dateRange.range_unavailable"));
        return;
      }
      if (!isControlled) setInternalTo(dateStr);
      onDateSelect?.(selectedFrom, dateStr);
      return;
    }

    if (!selectedFrom || (selectedFrom && selectedTo)) {
      if (!isControlled) {
        setInternalFrom(dateStr);
        setInternalTo(null);
      }
      onDateSelect?.(dateStr, "");
    } else {
      if (dateStr < selectedFrom) {
        if (!isControlled) {
          setInternalFrom(dateStr);
          setInternalTo(null);
        }
        onDateSelect?.(dateStr, "");
        return;
      }

      if (dateStr === selectedFrom) {
        const duration = 1;
        if (duration < minDays) {
          setValidationError(minBookingError(duration));
          return;
        }
        if (!isControlled) {
          setInternalTo(dateStr);
        }
        onDateSelect?.(selectedFrom, dateStr);
        return;
      }

      const duration = daysBetween(selectedFrom, dateStr);
      if (duration < minDays) {
        setValidationError(minBookingError(duration));
        return;
      }

      if (hasBlockedDateInRange(selectedFrom, dateStr, availability.blockedDates)) {
        setValidationError(t("dateRange.range_unavailable"));
        return;
      }

      if (!isControlled) {
        setInternalTo(dateStr);
      }
      onDateSelect?.(selectedFrom, dateStr);
    }
  }, [selectedFrom, selectedTo, minDays, availability.blockedDates, onDateSelect, isControlled, lockFrom, rentalType]);

  const renderMonth = (year: number, month: number) => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfWeek(year, month);
    const cells: React.ReactElement[] = [];

    for (let i = 0; i < firstDay; i++) {
      cells.push(<div key={`empty-${i}`} className="h-11 w-11 sm:h-9 sm:w-9" />);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const isPast = date < earliestDate;
      const blocked = isDateBlocked(date, availability.blockedDates);
      // Task #2940 follow-up — in lockFrom mode, anything before the
      // locked anchor is unselectable too.
      const beforeLocked = lockFrom && !!selectedFrom && dateStr < selectedFrom;
      const isStart = dateStr === selectedFrom;
      const isEnd = dateStr === selectedTo && selectedTo !== selectedFrom;
      const isSameDay = selectedFrom && selectedTo && selectedFrom === selectedTo && dateStr === selectedFrom;
      const isInRange = selectedFrom && selectedTo && selectedFrom !== selectedTo && dateStr > selectedFrom && dateStr < selectedTo;
      const isToday = date.toDateString() === today.toDateString();
      // Task #2974 — when the start pill is locked and the original
      // start day is in the past (typical of a same-day booking after
      // pickup time has passed), the locked anchor still needs to
      // render as the green fixed pill instead of a strikethrough.
      // We keep it un-clickable (the lock is the whole point) but
      // strip the "disabled" visual so the pill is still visible to
      // the user. Same idea for any past day that sits inside the
      // currently-selected range — it's still part of the trip.
      const isLockedStart = lockFrom && isStart;
      const isPastInRange = isPast && (isInRange || isEnd || isSameDay);
      const disabled = isLockedStart || (isPast && !isPastInRange) || blocked || beforeLocked;
      const visuallyMuted = disabled && !isLockedStart && !isPastInRange;

      cells.push(
        <button
          key={d}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && handleDayClick(dateStr)}
          className={cn(
            "relative flex h-11 w-11 sm:h-9 sm:w-9 items-center justify-center text-sm transition-colors",
            visuallyMuted && "cursor-not-allowed text-muted-foreground/40 line-through",
            isLockedStart && "cursor-not-allowed",
            blocked && !isPast && "bg-destructive/5",
            !disabled && !isStart && !isEnd && !isSameDay && !isInRange && "rounded-full hover:bg-muted",
            // Selected dates use BRAND_GREEN_LIGHT (#2d6a4f) rather than the
            // canonical primary (#1B4332). At a 36px pill the deeper #1B4332
            // (L=18%) reads as near-black to the eye — there's not enough
            // green chroma visible at that size. #2d6a4f (L=30%) is the same
            // hue family, ~12% brighter, and reads unambiguously as green.
            // The full-size brand CTAs across the app keep #1B4332 so brand
            // identity isn't diluted; this token is calendar-pill only.
            isSameDay && "rounded-full bg-brand-light text-white font-semibold",
            isStart && !isSameDay && "rounded-l-full bg-brand-light text-white font-semibold",
            isEnd && !isSameDay && "rounded-r-full bg-brand-light text-white font-semibold",
            isInRange && "bg-brand-light/15",
            isToday && !isStart && !isEnd && !isSameDay && "font-semibold text-brand-light",
          )}
        >
          {d}
        </button>
      );
    }

    return (
      <div>
        <MonthHeading className="mb-2 text-center text-sm font-semibold text-foreground">
          {formatMonth(year, month)}
        </MonthHeading>
        <div className="grid grid-cols-7 gap-y-0.5">
          {WEEKDAYS.map(w => (
            <div key={w} className="flex h-8 items-center justify-center text-xs font-medium text-muted-foreground">
              {w}
            </div>
          ))}
          {cells}
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="mb-3 flex items-center justify-between">
        <button onClick={handlePrev} className="inline-flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full bg-muted/60 hover:bg-muted text-foreground" aria-label={t("dateRange.previous_month")}>
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-xs text-muted-foreground">
          {selectedFrom && selectedTo && t("dateRange.days_selected", { count: daysBetween(selectedFrom, selectedTo) })}
          {selectedFrom && !selectedTo && t("dateRange.select_dropoff_date")}
          {!selectedFrom && t("dateRange.select_dates")}
          {minDays > 1 && ` · ${t("dateRange.min_inline", { count: minDays })}`}
          {advanceNotice > 0 && ` · ${t("dateRange.notice_inline", { count: advanceNotice })}`}
        </span>
        <button onClick={handleNext} className="inline-flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full bg-muted/60 hover:bg-muted text-foreground" aria-label={t("dateRange.next_month")}>
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="grid gap-6 sm:grid-cols-2">
        {months.map(m => (
          <div key={`${m.year}-${m.month}`}>{renderMonth(m.year, m.month)}</div>
        ))}
      </div>
      {validationError && (
        <div className="mt-3 flex items-start gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{validationError}</span>
        </div>
      )}
    </div>
  );
}
